import {
  createTransactionRequestService,
  getTransactionRequestDetailsService,
  getTransactionRequestListService,
} from '@/services/transactionRequests';
import {
  CreateTransactionRequest,
  TRANSACTION_REQUEST_STATUS,
  queryKeys as transactionRequestQueryKeys,
} from '@/types/transactionRequests';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';
import { queryKeys as transactionQueryKeys } from '@/types/transactions';

type UseGetTransactionRequestsListProps = {
  status: TRANSACTION_REQUEST_STATUS | TRANSACTION_REQUEST_STATUS[];
  bankAccountUUID?: string;
  limit?: number;
  page?: number;
};

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [transactionRequestQueryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [transactionRequestQueryKeys.details],
  });
}

export const useGetTransactionRequestsList = ({
  bankAccountUUID,
  status,
  limit = 20,
  page = 0,
}: UseGetTransactionRequestsListProps) => {
  const bankAccountIdQueryParam = bankAccountUUID
    ? `bank_account_id=${bankAccountUUID}`
    : '';
  const statusQueryParam =
    typeof status === 'number'
      ? `&status=${status}`
      : `&status=${status.join('&status=')}`;
  const queryParams = `page=${page}&records_per_page=${limit}&${bankAccountIdQueryParam}${statusQueryParam}`;

  const { data, isPending } = useQuery({
    queryKey: [transactionRequestQueryKeys.list, queryParams],
    queryFn: () => getTransactionRequestListService(queryParams),
    retry: false,
  });

  return {
    transactionRequests: data?.records || [],
    totalRecordCount: data?.totalRecordCount || 0,
    isTransactionRequestsListPending: isPending,
  };
};

export const useGetTransactionRequestsDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [transactionRequestQueryKeys.details, uuid],
    queryFn: () => getTransactionRequestDetailsService(uuid),
    retry: false,
  });

  return {
    transactionRequestDetails: data,
    isTransactionRequestDetailsPending: isPending,
  };
};

export const useCancelTransactionRequests = (uuid: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => getTransactionRequestDetailsService(uuid),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    cancelTransactionRquest: mutateAsync,
    isCancelTransactionRequestPending: isPending,
  };
};

export const useCreateTransactionRequest = () => {
  const queryClient = useQueryClient();
  const idempotencyKey = useIdempotencyKeyGenerator();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: CreateTransactionRequest) =>
      createTransactionRequestService({
        ...data,
        idempotencyKey,
      }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [transactionRequestQueryKeys.list],
      });
      queryClient.invalidateQueries({
        queryKey: [transactionRequestQueryKeys.details],
      });
      queryClient.invalidateQueries({
        queryKey: [transactionQueryKeys.list],
      });
      queryClient.invalidateQueries({
        queryKey: [transactionQueryKeys.details],
      });
    },
  });

  return {
    createTransactionRequest: mutateAsync,
    isCreateTransactionRequestPending: isPending,
  };
};
